/**
 * MOCK API — versión `core-admin-demo` para landing.
 *
 * Reemplaza el `api.ts` original. Todas las llamadas que antes iban a
 * `fetch(apiUrl + path)` ahora pasan por `mockRouter` que matchea por
 * patrón y devuelve datos sembrados desde `./mocks`.
 *
 * No hace network. Nunca tira. Para paths no matcheados devuelve
 * `{ success: true, data: [] }` para que la UI muestre estados vacíos
 * en vez de spinners infinitos.
 *
 * Las firmas de `adminApi` y `tenantApi` se mantienen 1:1 con el
 * original para que ningún componente tenga que cambiar.
 */
import { mockRouter, mockRouterRaw } from './mocks/router'

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

const SIMULATED_DELAY_MS = 180 // suficiente para ver loading states sin que aburra

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

async function call<T>(path: string, _headers: HeadersInit, opts: RequestOpts = {}): Promise<T> {
  await delay(SIMULATED_DELAY_MS)
  const result = mockRouter({
    path,
    method: opts.method ?? 'GET',
    body: opts.body,
  })
  return result as T
}

async function callRaw(path: string, _headers: HeadersInit, opts: RequestOpts = {}): Promise<string> {
  await delay(SIMULATED_DELAY_MS)
  return mockRouterRaw({ path, method: opts.method ?? 'GET' })
}

/** /api/admin/* — superadmin paths. */
export const adminApi = {
  get: <T>(path: string, opts?: RequestOpts) => call<T>(path, {}, opts),
  post: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    call<T>(path, {}, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    call<T>(path, {}, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    call<T>(path, {}, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts?: RequestOpts) =>
    call<T>(path, {}, { ...opts, method: 'DELETE' }),
}

/** /api/v1/* — tenant-scoped paths. */
export const tenantApi = {
  get: <T>(path: string, opts?: RequestOpts) => call<T>(path, {}, opts),
  getRaw: (path: string, opts?: RequestOpts) => callRaw(path, {}, opts),
  post: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    call<T>(path, {}, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    call<T>(path, {}, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    call<T>(path, {}, { ...opts, method: 'PUT', body }),
  del: <T>(path: string, opts?: RequestOpts) =>
    call<T>(path, {}, { ...opts, method: 'DELETE' }),
  delete: <T>(path: string, opts?: RequestOpts) =>
    call<T>(path, {}, { ...opts, method: 'DELETE' }),
}
